import * as React from 'react';
import { Layout } from 'antd';
import { Switch, Router, Route, Redirect, useLocation } from 'react-router';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ConfirmNewPasswordContainer } from 'features/user/containers/ConfirmNewPasswordContainer';
import { OrganisationSelectModalContainer } from 'features/user/containers/OrganisationSelectModalContainer';
import { getAuthenticatedStatus, getTokenStatus } from 'features/auth/selectors/authSelectors';
import { VerifyExistingExecutiveContainer } from 'features/auth/containers/VerifyExistingExecutiveContainer';
import { UserOrganisation, UserPaths } from 'features/user/models/userModel';
import { history } from 'config/config';
import { navigationRoutes } from 'config/navigation';
import { FullPageSpinner, CustomRoute } from 'common/components';
import { getDateRangeContractDashboard } from 'features/contract/selectors/contractSelectors';
import {
    getOrganisationCurrentSubscription,
    getUserFunctionalEmailsConsent,
    isAdmin,
    isTenant,
} from 'features/user/selectors/userSelectors';
import { breakpoints, colors, sizes } from 'theme/variables';
import { MobileMenu, SideMenu } from 'common/components/layout';
import { AuthContainer } from 'features/auth/containers/AuthContainer';
import { DashboardPaths } from 'features/dashboard/models/dashboardModel';
import { UploadPaths } from 'features/upload/models/uploadModels';

import { AppState } from '../appState';
import { EditPaths } from 'features/edit/models/editModel';
const PageLayout = styled(Layout)`
    && {
        background: #f5f7fa;
        min-height: 100vh;
    }
`;

const ContentWrapper = styled(Layout.Content)`
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - ${sizes.pageHeaderSmall});
    background: ${colors.primaryWhite};
    padding: 0;
    flex: 1;
    overflow-x: hidden;

    ${breakpoints.small} {
        min-height: calc(100vh - ${sizes.pageHeader});
    }

    ${breakpoints.medium} {
        min-height: 100vh;
        margin: 10px 10px 10px 0;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    }
`;

/* Wrap the route Switch so each navigation re-mounts and the fade-in
   animation plays — feels smoother than a hard route switch. */
const RouteFade = styled.div`
    display: contents;

    @keyframes cenefitsPageFadeIn {
        from {
            opacity: 0;
            transform: translateY(4px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    > * {
        animation: cenefitsPageFadeIn 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    }
`;

const RoutedContent: React.FunctionComponent = ({ children }) => {
    const { pathname } = useLocation();
    return <RouteFade key={pathname}>{children}</RouteFade>;
};

interface AppProps {
    isAuthenticated: boolean;
    isTokenChecked: boolean;
    isUserAdmin: boolean;
    isUserTenant: boolean;
    isFunctionalEmailsConsent?: boolean;
    activeOrganisation?: UserOrganisation;
}

const AppRouterComponent: React.FunctionComponent<AppProps> = ({
    isAuthenticated,
    isTokenChecked,
    isUserAdmin,
    isUserTenant,
    isFunctionalEmailsConsent,
    activeOrganisation,
}) => {
    const subscription = useSelector(getOrganisationCurrentSubscription);
    const isExpired = subscription?.status === 'EXPIRED';
    let defaultRedirectRoute: string = UploadPaths.PendingEvidence;
    if (isExpired) {
        defaultRedirectRoute = isUserAdmin ? EditPaths.Subscriptions : UserPaths.EditProfile;
    }

    if (!isFunctionalEmailsConsent) {
        defaultRedirectRoute = DashboardPaths.Benefits;
    }
    return (
        <Router history={history}>
            <Switch>
                <Route path={UserPaths.SignIn} component={AuthContainer} />
                <Route>
                    <PageLayout>
                        <Layout>
                            <MobileMenu isAuthenticated={isAuthenticated} />
                            <Layout>
                                <SideMenu hideMenu={false} />
                                <OrganisationSelectModalContainer />
                                <ContentWrapper>
                                    <RoutedContent>
                                        <Switch>
                                            {navigationRoutes.map((route, i) => (
                                                <CustomRoute
                                                    key={`${route.path}${i}`}
                                                    isAuthenticated={isAuthenticated}
                                                    isTokenChecked={isTokenChecked}
                                                    secured
                                                    activeOrganisation={activeOrganisation}
                                                    isFunctionalEmailsConsent={
                                                        isFunctionalEmailsConsent
                                                    }
                                                    isSubscriptionExpired={isExpired}
                                                    isUserAdmin={isUserAdmin}
                                                    isUserTenant={isUserTenant}
                                                    {...route}
                                                />
                                            ))}
                                            <Route
                                                path={`${UserPaths.ConfirmNewPassword}/:token`}
                                                component={ConfirmNewPasswordContainer}
                                            />
                                            <Route
                                                path={UserPaths.VerifyExistingExecutive}
                                                render={() =>
                                                    !isTokenChecked ? (
                                                        <FullPageSpinner />
                                                    ) : (
                                                        <VerifyExistingExecutiveContainer
                                                            getDateRangeContractDashboard={
                                                                getDateRangeContractDashboard
                                                            }
                                                        />
                                                    )
                                                }
                                            />
                                            <Redirect exact from="/" to={defaultRedirectRoute} />
                                        </Switch>
                                    </RoutedContent>
                                </ContentWrapper>
                            </Layout>
                        </Layout>
                    </PageLayout>
                </Route>
            </Switch>
        </Router>
    );
};

function mapStateToProps(state: AppState): AppProps {
    return {
        isAuthenticated: getAuthenticatedStatus(state),
        isTokenChecked: getTokenStatus(state),
        isFunctionalEmailsConsent: getUserFunctionalEmailsConsent(state),
        activeOrganisation: state.user.activeOrganisation,
        isUserAdmin: isAdmin(state),
        isUserTenant: isTenant(state),
    };
}

export const AppRouter = connect(mapStateToProps)(AppRouterComponent);
